'use client';

import { useContentfulLiveUpdates } from '@contentful/live-preview/react';
import { TypeBlockImage, TypePageCaseStudy } from '@fm-nx/contentful-types';

import Nav from '../../../components/nav';
import { lazy } from 'react';
import { PageIntroLevel2Display } from '@fm-nx/ui';
const SlideshowCaseStudyDetail = lazy(
    () => import('../../../components/slideshow-case-study-detail/slideshow-case-study-detail')
);
const BlockRenderer = lazy(() => import('@fm-nx/ui').then((mod) => ({ default: mod.BlockRenderer })));
const BlockImage = lazy(() => import('@fm-nx/ui').then((mod) => ({ default: mod.BlockImage })));
const DarkCircleBackground = lazy(() => import('@fm-nx/ui').then((mod) => ({ default: mod.DarkCircleBackground })));
const FadeOnScroll = lazy(() => import('@fm-nx/ui').then((mod) => ({ default: mod.FadeOnScroll })));
const HeaderGroup = lazy(() => import('@fm-nx/ui').then((mod) => ({ default: mod.HeaderGroup })));
const NumberedBlocks = lazy(() => import('@fm-nx/ui').then((mod) => ({ default: mod.NumberedBlocks })));

interface CaseStudyDetailProps {
    page: TypePageCaseStudy<'WITHOUT_UNRESOLVABLE_LINKS'>;
    preview: boolean;
}

export default function CaseStudyDetail({ page, preview }: CaseStudyDetailProps) {
    const updatedPage = useContentfulLiveUpdates(page);
    const { clientIntroductionImage, services, solutions, slideshowPage, technologies, heroImage } = page.fields;

    const clientIntroImage = {
        fields: { images: [clientIntroductionImage] },
        metadata: { tags: [] },
        sys: {
            id: 'mock',
            type: 'Entry',
            createdAt: '2022-01-27T19:38:01.651Z',
            updatedAt: '2023-08-15T21:59:21.597Z',
            contentType: {
                sys: { type: 'Link', linkType: 'ContentType', id: 'blockImage' },
            },
            locale: 'en-US',
            revision: 'test',
            space: 'test',
            environment: 'env',
        },
    } as unknown as TypeBlockImage<'WITHOUT_UNRESOLVABLE_LINKS'>;

    const heroImageFragment = heroImage
        ? {
              __typename: 'ImageWrapper' as const,
              sys: { id: heroImage.sys.id, __typename: 'Sys' as const },
              cloudinaryMedia: heroImage.fields.cloudinaryMedia,
              altText: heroImage.fields.altText || '',
              title: heroImage.fields.title || '',
              description: heroImage.fields.description || '',
          }
        : null;

    return (
        <div>
            <Nav variant="dark" preview={preview} />
            <div className="pb-20 lg:pb-40">
                <PageIntroLevel2Display
                    entryId={updatedPage.sys.id}
                    title="Case Studies"
                    headerText={updatedPage?.fields.title}
                    headerParagraph={updatedPage?.fields.summary}
                    image={heroImageFragment}
                    backgroundColor="blue-100"
                />
                <div className="mt-10 md:mt-0">
                    <FadeOnScroll>
                        <NumberedBlocks
                            blocks={[
                                {
                                    header: 'The Challenge',
                                    fact: updatedPage?.fields.challenge,
                                },
                                {
                                    header: 'The Approach',
                                    fact: updatedPage?.fields.approach,
                                },
                                {
                                    header: 'The Outcome',
                                    fact: updatedPage?.fields.outcome,
                                },
                            ]}
                        />
                    </FadeOnScroll>
                </div>

                <DarkCircleBackground>
                    <HeaderGroup
                        overline="Client Introduction"
                        mode="dark"
                        headerText={updatedPage?.fields.clientIntroductionHeader}
                        paragraph={updatedPage?.fields.clientIntroductionBody}
                    />

                    <div>
                        {clientIntroductionImage && (
                            <div className="my-28">
                                <BlockImage {...clientIntroImage} />
                            </div>
                        )}
                    </div>
                    <div className="space-y-20">
                        <div className="">
                            <HeaderGroup
                                overline="Project at a Glance"
                                mode="dark"
                                headerText={`What Our Team Provided`}
                            />
                        </div>
                        <div>
                            <HeaderGroup overline="Services" mode="dark" pageLinks={services} />
                        </div>
                        {solutions && solutions.length > 0 && (
                            <div className="">
                                <HeaderGroup
                                    mode="dark"
                                    listItems={solutions.map((solution) => solution?.fields.title)}
                                    overline={`Solutions`}
                                />
                            </div>
                        )}
                        <div className="">
                            <HeaderGroup mode="dark" pageLinks={technologies} overline={`Technologies`} />
                        </div>
                    </div>
                </DarkCircleBackground>
                <BlockRenderer block={page.fields.sections} />

                <SlideshowCaseStudyDetail page={page} slideshowPage={slideshowPage} />
            </div>
        </div>
    );
}
